import React, { FC, useEffect, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { usePathname } from 'next/navigation';

import { EditableComponent } from '@adobe/aem-react-editable-components';

import { Heading, Link, RichText, Text, Types } from '@marriott/mi-ui-library';
import { addClassToBlankTargetLinks, EMAIL_DATA_KEY, mergeMultipleParams } from '../../modules';
import { useAccountPersistentStore } from '../../modules/store/accountPersistentStore';
import { useStore } from '../../modules/store/memberLevelStore';
import { replaceText } from '../../modules/utils/promotionHelper';
import { ForgotPasswordEmailSentProps } from './ForgotPasswordEmailSent.types';
import { StyledForgotPasswordEmailSent, StyledCustomMessage } from './ForgotPasswordEmailSent.styles';

export const ForgotPasswordEmailSent: FC<ForgotPasswordEmailSentProps> = (props: ForgotPasswordEmailSentProps) => {
  const pathName = usePathname();
  const isError = props?.model?.isErrorPage;
  const [emailData, setEmailData] = useState<string | null>('');
  const [redirectParam, setRedirectParam] = useState('');
  const { isForgotPasswordSuccessEaa, showCustomMessage } = useStore(state => state);
  const { setIsEAAEnrollFailed, isEAAEnrollFailed } = useAccountPersistentStore(state => state);
  const isEmailPasswordPage = pathName?.includes('/emailPassword');
  const [isEnrollFailed, setIsEnrollFailed] = useState(false);
  const [domRendered, setDomRendered] = useState(false);

  useEffect(() => {
    setDomRendered(true);
    if (!showCustomMessage) {
      const customerEmailData = sessionStorage?.getItem(EMAIL_DATA_KEY);
      setEmailData(customerEmailData);
    }
    setIsEnrollFailed(!!isEAAEnrollFailed);
    isError && setIsEAAEnrollFailed?.(false);
    const urlParams = new URLSearchParams(window.location.search);
    const redirectUrl = urlParams.get('redirect_uri') || urlParams.get('redirectURI') || '';
    if (redirectUrl) {
      const redirectUrlParams = new URLSearchParams(new URL(redirectUrl)?.search);
      urlParams.delete('redirect_uri');
      urlParams.delete('redirectURI');
      const mergedParams = mergeMultipleParams(redirectUrlParams?.toString(), urlParams?.toString());
      setRedirectParam(redirectUrl?.split('?')[0] + (mergedParams ? mergedParams : ''));
    }
  }, []);

  return (
    <div data-component-name="o-account-forgotpasswordemailsent" data-testid="forgotpasswordemailsent">
      {showCustomMessage || isError ? (
        <StyledCustomMessage className="container px-0">
          <div className={clsx('mx-auto col-md-8 col-12', isError ? 'error-message px-0' : 'col-xl-5')}>
            <div className={clsx(isError && 'custom-error-wrapper', 'custom-message-wrapper')}>
              <Heading
                variation={Types.headingType.title}
                titleText={props?.model?.heading}
                fontSize={Types.size.small}
              />
              <div className={clsx('mb-5', isError ? 'mt-3' : 'mt-2')}>
                {isError && !redirectParam && !isEnrollFailed ? (
                  <RichText
                    text={
                      domRendered
                        ? addClassToBlankTargetLinks(props?.model?.contactUsLabel || '')
                        : props?.model?.contactUsLabel || ''
                    }
                    componentId={'contactUsLabel'}
                    customClass="t-subtitle-l"
                  />
                ) : (
                  <Heading
                    titleText={props?.model?.description}
                    variation={Types.headingType.subtitle}
                    fontSize={Types.size.large}
                  />
                )}
              </div>
              {isError && redirectParam && (
                <div className="mb-5">
                  <Link
                    text={props?.model?.goBackCtaLabel || ''}
                    linkClassName="m-link-tertiary-button-external t-font-m"
                    linkHref={redirectParam || ''}
                    target={'_blank'}
                  />
                </div>
              )}
              <Text
                copyText={replaceText(props?.model?.copyright || '', [String(moment().year())])}
                fontSize={Types.size.extraSmall}
                element={Types.tags.span}
              />
            </div>
          </div>
        </StyledCustomMessage>
      ) : (
        (props?.isAuthorMode || isForgotPasswordSuccessEaa || isEmailPasswordPage) && (
          <StyledForgotPasswordEmailSent>
            <div className="container spacing-top-bottom px-0">
              <Heading
                variation={Types.headingType.title}
                titleText={props?.model?.heading}
                customClass={'t-title-s'}
              />
              <div className="mt-4 mb-5">
                <Text
                  copyText={props?.model?.title}
                  fontSize={Types.size.large}
                  customClass={'t-subtitle-m'}
                  element={Types.tags.span}
                />
                <br />
                <div className="pb-5">
                  <RichText
                    text={
                      isEmailPasswordPage
                        ? props?.model?.genericDescription || ''
                        : props?.model?.description?.replace(
                            '{0}',
                            emailData ?? sessionStorage?.getItem(EMAIL_DATA_KEY) ?? ''
                          )
                    }
                    customClass={'t-font-s'}
                    componentId="email-sent-error-text"
                  />
                </div>
                {isForgotPasswordSuccessEaa && (
                  <div className="mt-4 mt-md-5">
                    <Text
                      copyText={props?.model?.copyright?.replace('{0}', String(moment().year()))}
                      fontSize={Types.size.extraSmall}
                      customClass={'t-font-xs'}
                      element={Types.tags.span}
                    />
                  </div>
                )}
              </div>
            </div>
          </StyledForgotPasswordEmailSent>
        )
      )}
    </div>
  );
};

export const ForgotPasswordEmailSentConfig = {
  emptyLabel: 'ForgotPasswordEmailSent',
  isEmpty: false,
  resourceType: `mi-aem-account/components/content/forgotpasswordemailsent`,
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ForgotPasswordEmailSentEditable = (props: any) => {
  return (
    <EditableComponent config={ForgotPasswordEmailSentConfig} {...props}>
      <ForgotPasswordEmailSent {...props} />
    </EditableComponent>
  );
};
